/**
 * SummaryReview Page
 *
 * Medical History Summary review workflow for MHR.
 * Displays AI-generated summary with diagnoses, medications, procedures.
 * Allows verification, editing, conflict resolution, and approval.
 *
 * URL: /mhr/documents/:documentId/summary
 */

import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  FileText,
  AlertTriangle,
  AlertCircle,
  CheckCircle,
  ChevronDown,
  ChevronRight,
  Edit2,
  Plus,
  Download,
  Loader2,
  Shield,
  ShieldCheck,
  Clock,
  RefreshCw,
  User,
  Sparkles,
  X,
  Check,
  Activity,
  Pill,
  Scissors,
  CircleDot,
  Info,
  Trash2,
  Strikethrough,
  Undo2,
  PanelRightOpen,
  PanelRightClose,
} from 'lucide-react';
import { useMHR } from '../contexts';
import { Toast } from '../components/Toast';
import { Breadcrumb } from '../components/Breadcrumb';
import { PDFViewer } from '../components/DocumentViewer/PDFViewer';
import { PageNavigation } from '../components/DocumentViewer/PageNavigation';
import { ZoomControls } from '../components/DocumentViewer/ZoomControls';
import * as mhrApi from '../lib/api';
import type {
  MedicalHistorySummary,
  DiagnosisEntry,
  MedicationEntry,
  ProcedureEntry,
  ConflictingEntry,
  SummaryStatus,
  ConfidenceLevel,
  DiagnosisStatus,
  MedicationStatus,
  ProcedureStatus,
  ConflictType,
  MHRDocument,
} from '../types';

// =====================================================
// Types & Interfaces
// =====================================================

interface SummaryData {
  summary: MedicalHistorySummary;
  diagnoses: DiagnosisEntry[];
  medications: MedicationEntry[];
  procedures: ProcedureEntry[];
  conflicts: ConflictingEntry[];
  document?: {
    id: string;
    filename: string;
    page_count: number;
    patient_id: string;
  };
  patient?: {
    id: string;
    internal_id: string;
    mrn?: string;
    name?: string;
  };
}

interface EditEntryModalProps {
  isOpen: boolean;
  entryType: 'diagnosis' | 'medication' | 'procedure';
  entry: DiagnosisEntry | MedicationEntry | ProcedureEntry | null;
  onSave: (data: any) => Promise<void>;
  onClose: () => void;
}

interface AddEntryModalProps {
  isOpen: boolean;
  entryType: 'diagnosis' | 'medication' | 'procedure';
  summaryId: string;
  onSave: (data: any) => Promise<void>;
  onClose: () => void;
}

interface ResolveConflictModalProps {
  isOpen: boolean;
  conflict: ConflictingEntry | null;
  onResolve: (conflictId: string, resolution: string) => Promise<void>;
  onClose: () => void;
}

interface ExportDropdownProps {
  onExport: (format: 'pdf' | 'json') => void;
  isExporting: boolean;
}

interface ApprovalConfirmationModalProps {
  isOpen: boolean;
  onConfirm: () => void;
  onClose: () => void;
  isApproving: boolean;
}

// =====================================================
// Label Constants
// =====================================================

const STATUS_LABELS: Record<SummaryStatus, string> = {
  draft: 'Draft',
  reviewed: 'Reviewed',
  approved: 'Approved',
};

const CONFIDENCE_LABEL_MAP: Record<ConfidenceLevel, string> = {
  high: 'High Confidence',
  medium: 'Medium Confidence',
  low: 'Low Confidence',
};

const DIAGNOSIS_STATUS_LABEL_MAP: Record<DiagnosisStatus, string> = {
  active: 'Active',
  resolved: 'Resolved',
  historical: 'Historical',
  uncertain: 'Uncertain',
};

const MEDICATION_STATUS_LABEL_MAP: Record<MedicationStatus, string> = {
  current: 'Current',
  discontinued: 'Discontinued',
  historical: 'Historical',
  prn: 'PRN (As Needed)',
  uncertain: 'Uncertain',
};

const PROCEDURE_STATUS_LABEL_MAP: Record<ProcedureStatus, string> = {
  completed: 'Completed',
  planned: 'Planned',
  cancelled: 'Cancelled',
  uncertain: 'Uncertain',
};

// =====================================================
// Timeout Configuration
// =====================================================

const GENERATION_TIMEOUT = 300000; // 5 minutes for summary generation
const APPROVAL_TIMEOUT = 60000; // 60 seconds for approval

// Helper function to wrap promises with timeout
function withTimeout<T>(promise: Promise<T>, timeoutMs: number, operationName: string): Promise<T> {
  return Promise.race([
    promise,
    new Promise<T>((_, reject) =>
      setTimeout(
        () => reject(new Error(`${operationName} timed out after ${Math.round(timeoutMs / 1000)} seconds. Please try again.`)),
        timeoutMs
      )
    ),
  ]);
}

// =====================================================
// Helper Components
// =====================================================

function StatusBadge({ status }: { status: SummaryStatus }) {
  const colors: Record<SummaryStatus, string> = {
    draft: 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700',
    reviewed: 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800',
    approved: 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 border border-green-300 dark:border-green-700',
  };

  const icons: Record<SummaryStatus, React.ReactNode> = {
    draft: <Edit2 size={12} className="mr-1" />,
    reviewed: <Clock size={12} className="mr-1" />,
    approved: <ShieldCheck size={12} className="mr-1" />,
  };

  return (
    <span className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full ${colors[status]}`}>
      {icons[status]}
      {STATUS_LABELS[status]}
    </span>
  );
}

function ConfidenceBadge({ confidence }: { confidence: ConfidenceLevel }) {
  const colors: Record<ConfidenceLevel, string> = {
    high: 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300',
    medium: 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-300',
    low: 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300',
  };

  return (
    <span className={`px-2 py-0.5 text-xs font-medium rounded ${colors[confidence]}`}>
      {confidence}
    </span>
  );
}

function VerificationBadge({ verified, verifiedBy }: { verified: boolean; verifiedBy?: string }) {
  if (verified) {
    return (
      <span
        className="inline-flex items-center gap-1 text-xs text-green-600 dark:text-green-400"
        title={`Verified by ${verifiedBy || 'user'}`}
      >
        <ShieldCheck size={14} />
        Verified
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-xs text-gray-400 dark:text-gray-500">
      <Shield size={14} />
      Unverified
    </span>
  );
}

function PageLinks({
  pages,
  onPageClick,
}: {
  pages: number[];
  onPageClick: (page: number) => void;
}) {
  if (!pages || pages.length === 0) return null;

  return (
    <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
      <span>p.</span>
      {pages.map((page, idx) => (
        <span key={page}>
          <button
            onClick={() => onPageClick(page)}
            className="text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300 hover:underline"
          >
            {page}
          </button>
          {idx < pages.length - 1 && ', '}
        </span>
      ))}
    </div>
  );
}

function CollapsibleSection({
  title,
  icon: Icon,
  count,
  children,
  defaultOpen = true,
  iconBgColor = 'bg-gray-100 dark:bg-gray-700',
  iconColor = 'text-gray-600 dark:text-gray-400',
}: {
  title: string;
  icon: React.ElementType;
  count: number;
  children: React.ReactNode;
  defaultOpen?: boolean;
  iconBgColor?: string;
  iconColor?: string;
}) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-4 py-3 flex items-center justify-between bg-gray-50 dark:bg-gray-900/40 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors min-h-[44px] sm:min-h-0"
      >
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${iconBgColor}`}>
            <Icon className={`w-5 h-5 ${iconColor}`} />
          </div>
          <span className="font-semibold text-gray-900 dark:text-gray-100">{title}</span>
          <span className="text-sm text-gray-500 dark:text-gray-400">({count})</span>
        </div>
        {isOpen ? (
          <ChevronDown className="w-5 h-5 text-gray-400 dark:text-gray-500" />
        ) : (
          <ChevronRight className="w-5 h-5 text-gray-400 dark:text-gray-500" />
        )}
      </button>
      {isOpen && <div className="p-4">{children}</div>}
    </div>
  );
}

// =====================================================
// Inline Edit Component
// =====================================================

function InlineEdit({
  value,
  onSave,
  type = 'text',
  options,
  readOnly = false,
  className = '',
  displayClassName = '',
}: {
  value: string;
  onSave: (val: string) => void;
  type?: 'text' | 'select';
  options?: Array<{ value: string; label: string }>;
  readOnly?: boolean;
  className?: string;
  displayClassName?: string;
}) {
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);
  const inputRef = useRef<HTMLInputElement | HTMLSelectElement>(null);

  useEffect(() => {
    setEditValue(value);
  }, [value]);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      if (inputRef.current instanceof HTMLInputElement) {
        inputRef.current.select();
      } else if (inputRef.current instanceof HTMLSelectElement) {
        // Open the dropdown immediately so it's click → select (2 clicks, not 3)
        (inputRef.current as any).showPicker?.();
      }
    }
  }, [editing]);

  if (readOnly) {
    const displayText = type === 'select' && options
      ? options.find(o => o.value === value)?.label || value
      : value;
    return <span className={displayClassName}>{displayText || '--'}</span>;
  }

  const handleSave = () => {
    if (editValue !== value) {
      onSave(editValue);
    }
    setEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSave();
    if (e.key === 'Escape') {
      setEditValue(value);
      setEditing(false);
    }
  };

  if (editing) {
    if (type === 'select' && options) {
      return (
        <select
          ref={inputRef as React.RefObject<HTMLSelectElement>}
          value={editValue}
          onChange={e => {
            setEditValue(e.target.value);
            onSave(e.target.value);
            setEditing(false);
          }}
          onBlur={() => setEditing(false)}
          className={`border border-primary-300 dark:border-primary-700 rounded px-2 py-0.5 focus:outline-none focus:ring-1 focus:ring-primary-400 ${className} bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100`}
        >
          {options.map(o => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      );
    }
    return (
      <input
        ref={inputRef as React.RefObject<HTMLInputElement>}
        type="text"
        value={editValue}
        onChange={e => setEditValue(e.target.value)}
        onBlur={handleSave}
        onKeyDown={handleKeyDown}
        className={`border border-primary-300 dark:border-primary-700 rounded px-2 py-0.5 focus:outline-none focus:ring-1 focus:ring-primary-400 ${className} bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100`}
      />
    );
  }

  const displayText = type === 'select' && options
    ? options.find(o => o.value === value)?.label || value
    : value;

  return (
    <span
      onClick={() => setEditing(true)}
      className={`cursor-pointer hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded px-1 py-0.5 -mx-1 transition-colors ${displayClassName}`}
      title="Click to edit"
    >
      {displayText || <span className="text-gray-300 dark:text-gray-600 italic">--</span>}
    </span>
  );
}

// =====================================================
// Entry Card Components
// =====================================================

function DiagnosisCard({
  diagnosis,
  onFieldSave,
  onVerify,
  onStrikeOut,
  onUndoStrikeOut,
  onDelete,
  onPageClick,
  isVerifying,
  isReadOnly,
}: {
  diagnosis: DiagnosisEntry;
  onFieldSave: (field: string, value: string) => void;
  onVerify: () => void;
  onStrikeOut: () => void;
  onUndoStrikeOut: () => void;
  onDelete: () => void;
  onPageClick: (page: number) => void;
  isVerifying: boolean;
  isReadOnly: boolean;
}) {
  const isStruckOut = diagnosis.struck_out;

  const statusColor: Record<DiagnosisStatus, string> = {
    active: 'text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20',
    resolved: 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20',
    historical: 'text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-900/40',
    uncertain: 'text-yellow-600 dark:text-yellow-400 bg-yellow-50 dark:bg-yellow-900/20',
  };

  const statusOptions = Object.entries(DIAGNOSIS_STATUS_LABEL_MAP).map(([k, v]) => ({ value: k, label: v }));

  return (
    <div className={`p-4 border rounded-lg transition-colors ${isStruckOut ? 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 opacity-60' : 'border-gray-200 dark:border-gray-700 hover:border-primary-200 dark:hover:border-primary-800'}`}>
      <div className="flex items-start justify-between gap-4">
        <div className={`flex-1 min-w-0 ${isStruckOut ? 'line-through' : ''}`}>
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className="font-medium text-gray-900 dark:text-gray-100">
              <InlineEdit
                value={diagnosis.condition_name}
                onSave={val => onFieldSave('condition_name', val)}
                readOnly={isReadOnly || isStruckOut}
                className="text-sm font-medium"
                displayClassName="font-medium text-gray-900 dark:text-gray-100"
              />
            </h4>
            <InlineEdit
              value={diagnosis.status}
              onSave={val => onFieldSave('status', val)}
              type="select"
              options={statusOptions}
              readOnly={isReadOnly || isStruckOut}
              className="text-xs"
              displayClassName={`px-2 py-0.5 text-xs font-medium rounded ${statusColor[diagnosis.status]}`}
            />
            <ConfidenceBadge confidence={diagnosis.confidence} />
          </div>

          {diagnosis.icd_codes && diagnosis.icd_codes.length > 0 && (
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              ICD: {diagnosis.icd_codes.join(', ')}
            </p>
          )}

          <div className="text-sm text-gray-500 dark:text-gray-400 mt-1 flex items-center gap-1 flex-wrap">
            <span>Onset: </span>
            <InlineEdit
              value={diagnosis.onset_date || ''}
              onSave={val => onFieldSave('onset_date', val)}
              readOnly={isReadOnly || isStruckOut}
              className="text-sm"
              displayClassName="text-sm text-gray-500 dark:text-gray-400"
            />
            <span className="mx-1">|</span>
            <span>Resolved: </span>
            <InlineEdit
              value={diagnosis.resolution_date || ''}
              onSave={val => onFieldSave('resolution_date', val)}
              readOnly={isReadOnly || isStruckOut}
              className="text-sm"
              displayClassName="text-sm text-gray-500 dark:text-gray-400"
            />
          </div>

          {diagnosis.source_excerpts && diagnosis.source_excerpts.length > 0 && (
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-2 italic line-clamp-2">
              &quot;{diagnosis.source_excerpts[0]}&quot;
            </p>
          )}

          <div className="flex items-center gap-4 mt-2">
            <PageLinks pages={diagnosis.source_pages} onPageClick={onPageClick} />
            <VerificationBadge verified={!!diagnosis.verified_by} verifiedBy={diagnosis.verified_by} />
            {diagnosis.is_manual && (
              <span className="text-xs text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/20 px-2 py-0.5 rounded">
                Manual Entry
              </span>
            )}
          </div>
        </div>

        {isStruckOut && diagnosis.struck_out_reason && (
          <div className="flex-shrink-0 text-xs text-gray-500 dark:text-gray-400 italic max-w-[200px]">
            Struck out: {diagnosis.struck_out_reason}
          </div>
        )}

        <div className="flex items-center gap-1 flex-shrink-0">
          {isStruckOut && !isReadOnly && (
            <button
              onClick={onUndoStrikeOut}
              className="p-2 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
              title="Undo strikeout"
            >
              <Undo2 size={18} />
            </button>
          )}

          {!isStruckOut && !isReadOnly && !diagnosis.verified_by && (
            <button
              onClick={onVerify}
              disabled={isVerifying}
              className="p-2 text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors disabled:opacity-50"
              title="Verify entry"
            >
              {isVerifying ? <Loader2 size={18} className="animate-spin" /> : <Check size={18} />}
            </button>
          )}

          {!isStruckOut && !isReadOnly && (
            <button
              onClick={onStrikeOut}
              className="p-2 text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/20 rounded-lg transition-colors"
              title="Strike out diagnosis"
            >
              <Strikethrough size={18} />
            </button>
          )}

          {!isReadOnly && (
            <button
              onClick={onDelete}
              className="p-2 text-red-500 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
              title="Delete diagnosis"
            >
              <Trash2 size={18} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function MedicationCard({
  medication,
  onFieldSave,
  onVerify,
  onStrikeOut,
  onUndoStrikeOut,
  onDelete,
  onPageClick,
  isVerifying,
  isReadOnly,
}: {
  medication: MedicationEntry;
  onFieldSave: (field: string, value: string) => void;
  onVerify: () => void;
  onStrikeOut: () => void;
  onUndoStrikeOut: () => void;
  onDelete: () => void;
  onPageClick: (page: number) => void;
  isVerifying: boolean;
  isReadOnly: boolean;
}) {
  const isStruckOut = medication.struck_out;

  const statusColor: Record<MedicationStatus, string> = {
    current: 'text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20',
    discontinued: 'text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20',
    historical: 'text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-900/40',
    prn: 'text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/20',
    uncertain: 'text-yellow-600 dark:text-yellow-400 bg-yellow-50 dark:bg-yellow-900/20',
  };

  const statusOptions = Object.entries(MEDICATION_STATUS_LABEL_MAP).map(([k, v]) => ({ value: k, label: v }));

  return (
    <div className={`p-4 border rounded-lg transition-colors ${isStruckOut ? 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 opacity-60' : 'border-gray-200 dark:border-gray-700 hover:border-primary-200 dark:hover:border-primary-800'}`}>
      <div className="flex items-start justify-between gap-4">
        <div className={`flex-1 min-w-0 ${isStruckOut ? 'line-through' : ''}`}>
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className="font-medium text-gray-900 dark:text-gray-100">
              <InlineEdit
                value={medication.medication_name}
                onSave={val => onFieldSave('medication_name', val)}
                readOnly={isReadOnly || isStruckOut}
                className="text-sm font-medium"
                displayClassName="font-medium text-gray-900 dark:text-gray-100"
              />
            </h4>
            <InlineEdit
              value={medication.status}
              onSave={val => onFieldSave('status', val)}
              type="select"
              options={statusOptions}
              readOnly={isReadOnly || isStruckOut}
              className="text-xs"
              displayClassName={`px-2 py-0.5 text-xs font-medium rounded ${statusColor[medication.status]}`}
            />
            <ConfidenceBadge confidence={medication.confidence} />
          </div>

          {medication.generic_name && (
            <p className="text-sm text-gray-500 dark:text-gray-400">({medication.generic_name})</p>
          )}

          <div className="text-sm text-gray-600 dark:text-gray-400 mt-1 flex items-center gap-1 flex-wrap">
            <InlineEdit
              value={medication.dose || ''}
              onSave={val => onFieldSave('dose', val)}
              readOnly={isReadOnly || isStruckOut}
              className="text-sm"
              displayClassName="text-sm text-gray-600 dark:text-gray-400"
            />
            <span>-</span>
            <InlineEdit
              value={medication.frequency || ''}
              onSave={val => onFieldSave('frequency', val)}
              readOnly={isReadOnly || isStruckOut}
              className="text-sm"
              displayClassName="text-sm text-gray-600 dark:text-gray-400"
            />
            <span>(</span>
            <InlineEdit
              value={medication.route || ''}
              onSave={val => onFieldSave('route', val)}
              readOnly={isReadOnly || isStruckOut}
              className="text-sm"
              displayClassName="text-sm text-gray-600 dark:text-gray-400"
            />
            <span>)</span>
          </div>

          {medication.drug_class && (
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Class: {medication.drug_class}</p>
          )}

          {(medication.start_date || medication.end_date) && (
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {medication.start_date && `Started: ${medication.start_date}`}
              {medication.start_date && medication.end_date && ' | '}
              {medication.end_date && `Ended: ${medication.end_date}`}
            </p>
          )}

          <div className="flex items-center gap-4 mt-2">
            <PageLinks pages={medication.source_pages} onPageClick={onPageClick} />
            <VerificationBadge verified={!!medication.verified_by} verifiedBy={medication.verified_by} />
            {medication.is_manual && (
              <span className="text-xs text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/20 px-2 py-0.5 rounded">
                Manual Entry
              </span>
            )}
          </div>
        </div>

        {isStruckOut && medication.struck_out_reason && (
          <div className="flex-shrink-0 text-xs text-gray-500 dark:text-gray-400 italic max-w-[200px]">
            Struck out: {medication.struck_out_reason}
          </div>
        )}

        <div className="flex items-center gap-1 flex-shrink-0">
          {isStruckOut && !isReadOnly && (
            <button
              onClick={onUndoStrikeOut}
              className="p-2 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
              title="Undo strikeout"
            >
              <Undo2 size={18} />
            </button>
          )}

          {!isStruckOut && !isReadOnly && !medication.verified_by && (
            <button
              onClick={onVerify}
              disabled={isVerifying}
              className="p-2 text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors disabled:opacity-50"
              title="Verify entry"
            >
              {isVerifying ? <Loader2 size={18} className="animate-spin" /> : <Check size={18} />}
            </button>
          )}

          {!isStruckOut && !isReadOnly && (
            <button
              onClick={onStrikeOut}
              className="p-2 text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/20 rounded-lg transition-colors"
              title="Strike out medication"
            >
              <Strikethrough size={18} />
            </button>
          )}

          {!isReadOnly && (
            <button
              onClick={onDelete}
              className="p-2 text-red-500 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
              title="Delete medication"
            >
              <Trash2 size={18} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function ProcedureCard({
  procedure,
  onFieldSave,
  onVerify,
  onStrikeOut,
  onUndoStrikeOut,
  onDelete,
  onPageClick,
  isVerifying,
  isReadOnly,
}: {
  procedure: ProcedureEntry;
  onFieldSave: (field: string, value: string) => void;
  onVerify: () => void;
  onStrikeOut: () => void;
  onUndoStrikeOut: () => void;
  onDelete: () => void;
  onPageClick: (page: number) => void;
  isReadOnly: boolean;
  isVerifying: boolean;
}) {
  const isStruckOut = procedure.struck_out;

  const statusColor: Record<ProcedureStatus, string> = {
    completed: 'text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20',
    planned: 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20',
    cancelled: 'text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20',
    uncertain: 'text-yellow-600 dark:text-yellow-400 bg-yellow-50 dark:bg-yellow-900/20',
  };

  const statusOptions = Object.entries(PROCEDURE_STATUS_LABEL_MAP).map(([k, v]) => ({ value: k, label: v }));

  return (
    <div className={`p-4 border rounded-lg transition-colors ${isStruckOut ? 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 opacity-60' : 'border-gray-200 dark:border-gray-700 hover:border-primary-200 dark:hover:border-primary-800'}`}>
      <div className="flex items-start justify-between gap-4">
        <div className={`flex-1 min-w-0 ${isStruckOut ? 'line-through' : ''}`}>
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className="font-medium text-gray-900 dark:text-gray-100">
              <InlineEdit
                value={procedure.procedure_name}
                onSave={val => onFieldSave('procedure_name', val)}
                readOnly={isReadOnly || isStruckOut}
                className="text-sm font-medium"
                displayClassName="font-medium text-gray-900 dark:text-gray-100"
              />
            </h4>
            <InlineEdit
              value={procedure.status}
              onSave={val => onFieldSave('status', val)}
              type="select"
              options={statusOptions}
              readOnly={isReadOnly || isStruckOut}
              className="text-xs"
              displayClassName={`px-2 py-0.5 text-xs font-medium rounded ${statusColor[procedure.status]}`}
            />
            <ConfidenceBadge confidence={procedure.confidence} />
          </div>

          {procedure.cpt_codes && procedure.cpt_codes.length > 0 && (
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              CPT: {procedure.cpt_codes.join(', ')}
            </p>
          )}

          <div className="text-sm text-gray-600 dark:text-gray-400 mt-1 flex items-center gap-1">
            <span>Date: </span>
            <InlineEdit
              value={procedure.procedure_date || ''}
              onSave={val => onFieldSave('procedure_date', val)}
              readOnly={isReadOnly || isStruckOut}
              className="text-sm"
              displayClassName="text-sm text-gray-600 dark:text-gray-400"
            />
          </div>

          {(procedure.location || procedure.provider) && (
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {procedure.location}
              {procedure.location && procedure.provider && ' - '}
              {procedure.provider}
            </p>
          )}

          <div className="text-sm text-gray-600 dark:text-gray-400 mt-1 flex items-center gap-1">
            <span>Outcome: </span>
            <InlineEdit
              value={procedure.outcome || ''}
              onSave={val => onFieldSave('outcome', val)}
              readOnly={isReadOnly || isStruckOut}
              className="text-sm"
              displayClassName="text-sm text-gray-600 dark:text-gray-400"
            />
          </div>

          <div className="flex items-center gap-4 mt-2">
            <PageLinks pages={procedure.source_pages} onPageClick={onPageClick} />
            <VerificationBadge verified={!!procedure.verified_by} verifiedBy={procedure.verified_by} />
            {procedure.is_manual && (
              <span className="text-xs text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/20 px-2 py-0.5 rounded">
                Manual Entry
              </span>
            )}
          </div>
        </div>

        {isStruckOut && procedure.struck_out_reason && (
          <div className="flex-shrink-0 text-xs text-gray-500 dark:text-gray-400 italic max-w-[200px]">
            Struck out: {procedure.struck_out_reason}
          </div>
        )}

        <div className="flex items-center gap-1 flex-shrink-0">
          {isStruckOut && !isReadOnly && (
            <button
              onClick={onUndoStrikeOut}
              className="p-2 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
              title="Undo strikeout"
            >
              <Undo2 size={18} />
            </button>
          )}

          {!isStruckOut && !isReadOnly && !procedure.verified_by && (
            <button
              onClick={onVerify}
              disabled={isVerifying}
              className="p-2 text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors disabled:opacity-50"
              title="Verify entry"
            >
              {isVerifying ? <Loader2 size={18} className="animate-spin" /> : <Check size={18} />}
            </button>
          )}

          {!isStruckOut && !isReadOnly && (
            <button
              onClick={onStrikeOut}
              className="p-2 text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/20 rounded-lg transition-colors"
              title="Strike out procedure"
            >
              <Strikethrough size={18} />
            </button>
          )}

          {!isReadOnly && (
            <button
              onClick={onDelete}
              className="p-2 text-red-500 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
              title="Delete procedure"
            >
              <Trash2 size={18} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function ConflictAlert({
  conflict,
  onResolve,
}: {
  conflict: ConflictingEntry;
  onResolve: () => void;
}) {
  const typeLabels: Record<ConflictType, string> = {
    diagnosis: 'Diagnosis',
    medication: 'Medication',
    procedure: 'Procedure',
  };

  return (
    <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
      <div className="flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className="font-medium text-yellow-800 dark:text-yellow-300">
              {typeLabels[conflict.conflict_type]} Conflict
            </span>
            {conflict.resolved && (
              <span className="text-xs text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-900/40 px-2 py-0.5 rounded">
                Resolved
              </span>
            )}
          </div>
          <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">{conflict.description}</p>
          {conflict.resolution_notes && (
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-2 italic">
              Resolution: {conflict.resolution_notes}
            </p>
          )}
          {!conflict.resolved && (
            <button
              onClick={onResolve}
              className="mt-2 text-sm text-yellow-700 dark:text-yellow-300 hover:text-yellow-800 dark:hover:text-yellow-300 font-medium"
            >
              Resolve Conflict
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// =====================================================
// Modal Components
// =====================================================

function EditEntryModal({ isOpen, entryType, entry, onSave, onClose }: EditEntryModalProps) {
  const [formData, setFormData] = useState<any>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (entry) {
      setFormData({ ...entry });
    }
  }, [entry]);

  if (!isOpen || !entry) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      await onSave(formData);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const renderFields = () => {
    switch (entryType) {
      case 'diagnosis':
        return (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Condition Name</label>
              <input
                type="text"
                value={formData.condition_name || ''}
                onChange={e => setFormData({ ...formData, condition_name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Status</label>
              <select
                value={formData.status || 'active'}
                onChange={e => setFormData({ ...formData, status: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              >
                {Object.entries(DIAGNOSIS_STATUS_LABEL_MAP).map(([key, label]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Onset Date</label>
                <input
                  type="text"
                  value={formData.onset_date || ''}
                  onChange={e => setFormData({ ...formData, onset_date: e.target.value })}
                  placeholder="e.g., 2023-01"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Resolution Date</label>
                <input
                  type="text"
                  value={formData.resolution_date || ''}
                  onChange={e => setFormData({ ...formData, resolution_date: e.target.value })}
                  placeholder="e.g., 2024-06"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                />
              </div>
            </div>
          </>
        );

      case 'medication':
        return (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Medication Name</label>
              <input
                type="text"
                value={formData.medication_name || ''}
                onChange={e => setFormData({ ...formData, medication_name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Status</label>
              <select
                value={formData.status || 'current'}
                onChange={e => setFormData({ ...formData, status: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              >
                {Object.entries(MEDICATION_STATUS_LABEL_MAP).map(([key, label]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Dose</label>
                <input
                  type="text"
                  value={formData.dose || ''}
                  onChange={e => setFormData({ ...formData, dose: e.target.value })}
                  placeholder="e.g., 10mg"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Frequency</label>
                <input
                  type="text"
                  value={formData.frequency || ''}
                  onChange={e => setFormData({ ...formData, frequency: e.target.value })}
                  placeholder="e.g., Once daily"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                />
              </div>
            </div>
          </>
        );

      case 'procedure':
        return (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Procedure Name</label>
              <input
                type="text"
                value={formData.procedure_name || ''}
                onChange={e => setFormData({ ...formData, procedure_name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Status</label>
              <select
                value={formData.status || 'completed'}
                onChange={e => setFormData({ ...formData, status: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              >
                {Object.entries(PROCEDURE_STATUS_LABEL_MAP).map(([key, label]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Date</label>
              <input
                type="text"
                value={formData.procedure_date || ''}
                onChange={e => setFormData({ ...formData, procedure_date: e.target.value })}
                placeholder="e.g., 2023-05-15"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Outcome</label>
              <input
                type="text"
                value={formData.outcome || ''}
                onChange={e => setFormData({ ...formData, outcome: e.target.value })}
                placeholder="e.g., Successful"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              />
            </div>
          </>
        );
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-auto">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Edit {entryType.charAt(0).toUpperCase() + entryType.slice(1)}
            </h2>
            <button onClick={onClose} className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300">
              <X size={20} />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm text-red-700 dark:text-red-300">
              {error}
            </div>
          )}

          {renderFields()}

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Confidence</label>
            <select
              value={formData.confidence || 'medium'}
              onChange={e => setFormData({ ...formData, confidence: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            >
              {Object.entries(CONFIDENCE_LABEL_MAP).map(([key, label]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 min-h-[44px] sm:min-h-0"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 min-h-[44px] sm:min-h-0"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function AddEntryModal({ isOpen, entryType, summaryId, onSave, onClose }: AddEntryModalProps) {
  const [formData, setFormData] = useState<any>({
    confidence: 'medium',
    source_pages: [],
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setFormData({
        confidence: 'medium',
        source_pages: [],
        status: entryType === 'diagnosis' ? 'active' : entryType === 'medication' ? 'current' : 'completed',
      });
      setError(null);
    }
  }, [isOpen, entryType]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      await onSave({ ...formData, summary_id: summaryId });
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to add entry');
    } finally {
      setSaving(false);
    }
  };

  const renderFields = () => {
    switch (entryType) {
      case 'diagnosis':
        return (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Condition Name <span className="text-red-500 dark:text-red-400">*</span>
              </label>
              <input
                type="text"
                value={formData.condition_name || ''}
                onChange={e => setFormData({ ...formData, condition_name: e.target.value })}
                required
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Status</label>
              <select
                value={formData.status || 'active'}
                onChange={e => setFormData({ ...formData, status: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              >
                {Object.entries(DIAGNOSIS_STATUS_LABEL_MAP).map(([key, label]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
            </div>
          </>
        );

      case 'medication':
        return (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Medication Name <span className="text-red-500 dark:text-red-400">*</span>
              </label>
              <input
                type="text"
                value={formData.medication_name || ''}
                onChange={e => setFormData({ ...formData, medication_name: e.target.value })}
                required
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Status</label>
              <select
                value={formData.status || 'current'}
                onChange={e => setFormData({ ...formData, status: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              >
                {Object.entries(MEDICATION_STATUS_LABEL_MAP).map(([key, label]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Dose</label>
                <input
                  type="text"
                  value={formData.dose || ''}
                  onChange={e => setFormData({ ...formData, dose: e.target.value })}
                  placeholder="e.g., 10mg"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Frequency</label>
                <input
                  type="text"
                  value={formData.frequency || ''}
                  onChange={e => setFormData({ ...formData, frequency: e.target.value })}
                  placeholder="e.g., Once daily"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                />
              </div>
            </div>
          </>
        );

      case 'procedure':
        return (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Procedure Name <span className="text-red-500 dark:text-red-400">*</span>
              </label>
              <input
                type="text"
                value={formData.procedure_name || ''}
                onChange={e => setFormData({ ...formData, procedure_name: e.target.value })}
                required
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Status</label>
              <select
                value={formData.status || 'completed'}
                onChange={e => setFormData({ ...formData, status: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              >
                {Object.entries(PROCEDURE_STATUS_LABEL_MAP).map(([key, label]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Date</label>
              <input
                type="text"
                value={formData.procedure_date || ''}
                onChange={e => setFormData({ ...formData, procedure_date: e.target.value })}
                placeholder="e.g., 2023-05-15"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              />
            </div>
          </>
        );
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-auto">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Add {entryType.charAt(0).toUpperCase() + entryType.slice(1)}
            </h2>
            <button onClick={onClose} className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300">
              <X size={20} />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm text-red-700 dark:text-red-300">
              {error}
            </div>
          )}

          {renderFields()}

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Confidence</label>
            <select
              value={formData.confidence || 'medium'}
              onChange={e => setFormData({ ...formData, confidence: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            >
              {Object.entries(CONFIDENCE_LABEL_MAP).map(([key, label]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Source Pages</label>
            <input
              type="text"
              value={(formData.source_pages || []).join(', ')}
              onChange={e => {
                const pages = e.target.value
                  .split(',')
                  .map(p => parseInt(p.trim(), 10))
                  .filter(p => !isNaN(p));
                setFormData({ ...formData, source_pages: pages });
              }}
              placeholder="e.g., 1, 5, 12"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Comma-separated page numbers</p>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 min-h-[44px] sm:min-h-0"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 min-h-[44px] sm:min-h-0"
            >
              {saving ? 'Adding...' : 'Add Entry'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ResolveConflictModal({ isOpen, conflict, onResolve, onClose }: ResolveConflictModalProps) {
  const [resolution, setResolution] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setResolution('');
      setError(null);
    }
  }, [isOpen]);

  if (!isOpen || !conflict) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resolution.trim()) {
      setError('Please provide a resolution note');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      await onResolve(conflict.id, resolution);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to resolve conflict');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-lg w-full">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Resolve Conflict</h2>
            <button onClick={onClose} className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300">
              <X size={20} />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm text-red-700 dark:text-red-300">
              {error}
            </div>
          )}

          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
            <p className="text-sm text-yellow-800 dark:text-yellow-300">{conflict.description}</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Resolution Notes <span className="text-red-500 dark:text-red-400">*</span>
            </label>
            <textarea
              value={resolution}
              onChange={e => setResolution(e.target.value)}
              rows={4}
              placeholder="Describe how this conflict was resolved..."
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            />
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 min-h-[44px] sm:min-h-0"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 min-h-[44px] sm:min-h-0"
            >
              {saving ? 'Resolving...' : 'Mark Resolved'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ExportDropdown({ onExport, isExporting }: ExportDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        disabled={isExporting}
        className="inline-flex items-center gap-2 px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 min-h-[44px] sm:min-h-0"
      >
        {isExporting ? (
          <Loader2 size={18} className="animate-spin" />
        ) : (
          <Download size={18} />
        )}
        Export
        <ChevronDown size={16} />
      </button>

      {isOpen && !isExporting && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
          <div className="absolute right-0 mt-2 w-40 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg z-50">
            <button
              onClick={() => {
                onExport('pdf');
                setIsOpen(false);
              }}
              className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-t-lg min-h-[44px] sm:min-h-0"
            >
              Export as PDF
            </button>
            <button
              onClick={() => {
                onExport('json');
                setIsOpen(false);
              }}
              className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-b-lg min-h-[44px] sm:min-h-0"
            >
              Export as JSON
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function ApprovalConfirmationModal({
  isOpen,
  onConfirm,
  onClose,
  isApproving,
}: ApprovalConfirmationModalProps) {
  const [attestationChecked, setAttestationChecked] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setAttestationChecked(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-lg w-full">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-green-50 dark:bg-green-900/20">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-green-600 dark:text-green-400" />
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Approve Medical History Summary</h2>
            </div>
            <button onClick={onClose} className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300">
              <X size={20} />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-4">
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-medium text-yellow-800 dark:text-yellow-300">Important Notice</h3>
                <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
                  Approving this summary will lock it from further edits. This action cannot be undone.
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <h3 className="font-medium text-gray-900 dark:text-gray-100">By approving this summary, you attest that:</h3>
            <ul className="list-disc pl-5 space-y-1 text-sm text-gray-700 dark:text-gray-300">
              <li>You have reviewed all extracted diagnoses, medications, and procedures</li>
              <li>All identified conflicts have been resolved</li>
              <li>The information is accurate and complete to the best of your knowledge</li>
              <li>This summary is ready to be used for eligibility criteria evaluation</li>
            </ul>
          </div>

          <label className="flex items-start gap-3 p-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-900/40 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
            <input
              type="checkbox"
              checked={attestationChecked}
              onChange={e => setAttestationChecked(e.target.checked)}
              className="mt-0.5 h-4 w-4 text-green-600 dark:text-green-400 border-gray-300 dark:border-gray-600 rounded focus:ring-green-500"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">
              I confirm that I have reviewed this medical history summary and attest to its accuracy for clinical research purposes.
            </span>
          </label>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={isApproving}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 min-h-[44px] sm:min-h-0"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              disabled={!attestationChecked || isApproving}
              className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors min-h-[44px] sm:min-h-0"
            >
              {isApproving ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  Approving...
                </>
              ) : (
                <>
                  <ShieldCheck size={18} />
                  Approve Summary
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// =====================================================
// Main Component
// =====================================================

export function SummaryReview() {
  const { documentId } = useParams<{ documentId: string }>();
  const navigate = useNavigate();
  const { currentProtocol } = useMHR();

  // State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [summaryData, setSummaryData] = useState<SummaryData | null>(null);
  const [documentInfo, setDocumentInfo] = useState<MHRDocument | null>(null);
  // Patient info is available via documentInfo.patient if needed
  const [patientInfo, setPatientInfo] = useState<{ id: string; internal_id: string; mrn?: string; name?: string } | null>(null);
  // Suppress unused setter warning - may be used in future
  void setPatientInfo;

  // Action states
  const [generating, setGenerating] = useState(false);
  const [approving, setApproving] = useState(false);
  const [verifyingId, setVerifyingId] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  // Success notification state
  const [success, setSuccess] = useState<string | null>(null);
  const clearSuccess = useCallback(() => setSuccess(null), []);

  // Modal states
  const [addModal, setAddModal] = useState<{
    isOpen: boolean;
    entryType: 'diagnosis' | 'medication' | 'procedure';
  }>({ isOpen: false, entryType: 'diagnosis' });

  const [resolveModal, setResolveModal] = useState<{
    isOpen: boolean;
    conflict: ConflictingEntry | null;
  }>({ isOpen: false, conflict: null });

  const [approvalModalOpen, setApprovalModalOpen] = useState(false);

  // Document viewer panel state
  const [docViewerOpen, setDocViewerOpen] = useState(false);
  const [docViewerPage, setDocViewerPage] = useState(1);
  const [docViewerZoom, setDocViewerZoom] = useState(1.0);
  const [docViewerTotalPages, setDocViewerTotalPages] = useState(0);
  const [pdfUrl, setPdfUrl] = useState<string>('');
  const [pdfLoading, setPdfLoading] = useState(false);

  // Fetch document as blob URL when viewer opens (proxied through backend to avoid S3 CORS)
  useEffect(() => {
    if (!docViewerOpen || !documentId || pdfUrl) return;
    let cancelled = false;
    setPdfLoading(true);
    mhrApi.getDocumentViewUrl(documentId)
      .then(url => { if (!cancelled) setPdfUrl(url); })
      .catch(err => {
        console.error('[SummaryReview] Failed to load document:', err);
        if (!cancelled) setError('Failed to load document for viewing');
      })
      .finally(() => { if (!cancelled) setPdfLoading(false); });
    return () => { cancelled = true; };
  }, [docViewerOpen, documentId, pdfUrl]);

  // Revoke blob URL on unmount to free memory
  useEffect(() => {
    return () => {
      if (pdfUrl && pdfUrl.startsWith('blob:')) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [pdfUrl]);

  // Collect all pages that have findings for the quick-jump bar
  const pagesWithFindings = useMemo(() => {
    if (!summaryData) return [];
    const pages = new Set<number>();
    summaryData.diagnoses.forEach(d => d.source_pages?.forEach((p: number) => pages.add(p)));
    summaryData.medications.forEach(m => m.source_pages?.forEach((p: number) => pages.add(p)));
    summaryData.procedures.forEach(p => p.source_pages?.forEach((pg: number) => pages.add(pg)));
    return Array.from(pages).sort((a, b) => a - b);
  }, [summaryData]);

  // Load summary data
  const loadSummary = useCallback(async () => {
    if (!documentId) return;

    setLoading(true);
    setError(null);

    try {
      // Try to get existing summary
      const response = await mhrApi.getSummary(documentId);
      setSummaryData({
        summary: response.summary,
        diagnoses: response.diagnoses || [],
        medications: response.medications || [],
        procedures: response.procedures || [],
        conflicts: response.conflicts || [],
      });
    } catch (err: any) {
      console.error('[SummaryReview] Error loading summary:', err);
      // Check if it's a 404 (no summary exists)
      if (err.response?.status === 404) {
        setSummaryData(null);
      } else {
        setError(err.response?.data?.error || 'Failed to load summary');
      }
    }

    // Also try to load document info
    try {
      const docStatus = await mhrApi.getDocumentStatus(documentId);
      if (docStatus) {
        setDocumentInfo(docStatus as any);
      }
    } catch (docErr) {
      console.error('[SummaryReview] Error loading document:', docErr);
    }

    setLoading(false);
  }, [documentId]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  // Computed values - memoized to prevent unnecessary re-renders
  const diagnoses = useMemo(() => summaryData?.diagnoses || [], [summaryData?.diagnoses]);
  const medications = useMemo(() => summaryData?.medications || [], [summaryData?.medications]);
  const procedures = useMemo(() => summaryData?.procedures || [], [summaryData?.procedures]);
  const conflicts = useMemo(() => summaryData?.conflicts || [], [summaryData?.conflicts]);

  const hasUnresolvedConflicts = useMemo(() => {
    return conflicts.some(c => !c.resolved);
  }, [conflicts]);

  const canApprove = useMemo(() => {
    if (!summaryData) return false;
    return (
      summaryData.summary.status !== 'approved' &&
      !hasUnresolvedConflicts
    );
  }, [summaryData, hasUnresolvedConflicts]);

  const canProceedToCriteria = useMemo(() => {
    return summaryData?.summary.status === 'approved';
  }, [summaryData]);

  // Check if summary is approved (locked for editing)
  const isApproved = useMemo(() => {
    return summaryData?.summary.status === 'approved';
  }, [summaryData]);

  // Split entries by status
  const { activeDiagnoses, historicalDiagnoses } = useMemo(() => {
    return {
      activeDiagnoses: diagnoses.filter(d => d.status === 'active' || d.status === 'uncertain'),
      historicalDiagnoses: diagnoses.filter(d => d.status === 'resolved' || d.status === 'historical'),
    };
  }, [diagnoses]);

  const { currentMedications, historicalMedications } = useMemo(() => {
    return {
      currentMedications: medications.filter(m => m.status === 'current' || m.status === 'prn' || m.status === 'uncertain'),
      historicalMedications: medications.filter(m => m.status === 'discontinued' || m.status === 'historical'),
    };
  }, [medications]);

  const { completedProcedures, plannedProcedures } = useMemo(() => {
    return {
      completedProcedures: procedures.filter(p => p.status === 'completed' || p.status === 'cancelled'),
      plannedProcedures: procedures.filter(p => p.status === 'planned' || p.status === 'uncertain'),
    };
  }, [procedures]);

  // Handlers
  const handleGenerateSummary = async () => {
    if (!documentId) return;

    setGenerating(true);
    setError(null);

    try {
      const response = await withTimeout(
        mhrApi.generateSummary(documentId),
        GENERATION_TIMEOUT,
        'Summary generation'
      );
      setSummaryData({
        summary: response.summary,
        diagnoses: response.diagnoses || [],
        medications: response.medications || [],
        procedures: response.procedures || [],
        conflicts: response.conflicts || [],
      });
      setSuccess('Summary generated successfully');
    } catch (err: any) {
      console.error('[SummaryReview] Error generating summary:', err);
      setError(err.response?.data?.error || err.message || 'Failed to generate summary');
    } finally {
      setGenerating(false);
    }
  };

  const handleApproveSummary = async () => {
    if (!summaryData) return;

    setApproving(true);
    setError(null);

    try {
      const updatedSummary = await withTimeout(
        mhrApi.approveSummary(summaryData.summary.id),
        APPROVAL_TIMEOUT,
        'Summary approval'
      );
      setSummaryData(prev => prev ? {
        ...prev,
        summary: updatedSummary,
      } : null);
      setApprovalModalOpen(false);
      setSuccess('Summary approved successfully');
    } catch (err: any) {
      console.error('[SummaryReview] Error approving summary:', err);
      setError(err.response?.data?.error || err.message || 'Failed to approve summary');
    } finally {
      setApproving(false);
    }
  };

  // Open approval confirmation modal
  const handleOpenApprovalModal = () => {
    setApprovalModalOpen(true);
  };

  const handleVerifyEntry = async (
    entryType: 'diagnosis' | 'medication' | 'procedure',
    entryId: string
  ) => {
    if (!summaryData) return;

    setVerifyingId(entryId);

    try {
      // Map to plural form for API
      const apiEntryType = entryType === 'diagnosis' ? 'diagnoses' :
                          entryType === 'medication' ? 'medications' : 'procedures';
      await mhrApi.verifyEntry(summaryData.summary.id, apiEntryType, entryId);
      await loadSummary(); // Refresh to get updated data
      setSuccess('Entry verified successfully');
    } catch (err: any) {
      console.error('[SummaryReview] Error verifying entry:', err);
      setError(err.response?.data?.error || 'Failed to verify entry');
    } finally {
      setVerifyingId(null);
    }
  };

  const handleStrikeOut = async (diagnosisId: string) => {
    if (!summaryData) return;

    const reason = window.prompt('Reason for striking out this diagnosis:');
    if (!reason) return;

    try {
      await mhrApi.updateDiagnosis(summaryData.summary.id, diagnosisId, {
        struck_out: true,
        struck_out_reason: reason,
      } as any);

      // Optimistic update
      setSummaryData(prev => prev ? {
        ...prev,
        diagnoses: prev.diagnoses.map(d =>
          d.id === diagnosisId ? { ...d, struck_out: true, struck_out_reason: reason } : d
        ),
      } : null);
      setSuccess('Diagnosis struck out');
    } catch (err: any) {
      console.error('[SummaryReview] Error striking out diagnosis:', err);
      setError(err.response?.data?.error || 'Failed to strike out diagnosis');
    }
  };

  const handleUndoStrikeOut = async (diagnosisId: string) => {
    if (!summaryData) return;

    try {
      await mhrApi.updateDiagnosis(summaryData.summary.id, diagnosisId, {
        struck_out: false,
      } as any);

      // Optimistic update
      setSummaryData(prev => prev ? {
        ...prev,
        diagnoses: prev.diagnoses.map(d =>
          d.id === diagnosisId ? { ...d, struck_out: false, struck_out_reason: undefined, struck_out_by: undefined, struck_out_at: undefined } : d
        ),
      } : null);
      setSuccess('Strikeout undone');
    } catch (err: any) {
      console.error('[SummaryReview] Error undoing strikeout:', err);
      setError(err.response?.data?.error || 'Failed to undo strikeout');
    }
  };

  const handleDeleteDiagnosis = async (diagnosisId: string) => {
    if (!summaryData) return;

    try {
      await mhrApi.deleteDiagnosis(summaryData.summary.id, diagnosisId);

      // Optimistic update — remove from list
      setSummaryData(prev => prev ? {
        ...prev,
        diagnoses: prev.diagnoses.filter(d => d.id !== diagnosisId),
      } : null);
      setSuccess('Diagnosis removed');
    } catch (err: any) {
      console.error('[SummaryReview] Error deleting diagnosis:', err);
      setError(err.response?.data?.error || 'Failed to delete diagnosis');
    }
  };

  const handleStrikeOutMedication = async (medicationId: string) => {
    if (!summaryData) return;

    const reason = window.prompt('Reason for striking out this medication:');
    if (!reason) return;

    try {
      await mhrApi.updateMedication(summaryData.summary.id, medicationId, {
        struck_out: true,
        struck_out_reason: reason,
      } as any);

      setSummaryData(prev => prev ? {
        ...prev,
        medications: prev.medications.map(m =>
          m.id === medicationId ? { ...m, struck_out: true, struck_out_reason: reason } : m
        ),
      } : null);
      setSuccess('Medication struck out');
    } catch (err: any) {
      console.error('[SummaryReview] Error striking out medication:', err);
      setError(err.response?.data?.error || 'Failed to strike out medication');
    }
  };

  const handleUndoStrikeOutMedication = async (medicationId: string) => {
    if (!summaryData) return;

    try {
      await mhrApi.updateMedication(summaryData.summary.id, medicationId, {
        struck_out: false,
      } as any);

      setSummaryData(prev => prev ? {
        ...prev,
        medications: prev.medications.map(m =>
          m.id === medicationId ? { ...m, struck_out: false, struck_out_reason: undefined, struck_out_by: undefined, struck_out_at: undefined } : m
        ),
      } : null);
      setSuccess('Strikeout undone');
    } catch (err: any) {
      console.error('[SummaryReview] Error undoing medication strikeout:', err);
      setError(err.response?.data?.error || 'Failed to undo strikeout');
    }
  };

  const handleDeleteMedication = async (medicationId: string) => {
    if (!summaryData) return;

    try {
      await mhrApi.deleteMedication(summaryData.summary.id, medicationId);

      setSummaryData(prev => prev ? {
        ...prev,
        medications: prev.medications.filter(m => m.id !== medicationId),
      } : null);
      setSuccess('Medication removed');
    } catch (err: any) {
      console.error('[SummaryReview] Error deleting medication:', err);
      setError(err.response?.data?.error || 'Failed to delete medication');
    }
  };

  const handleStrikeOutProcedure = async (procedureId: string) => {
    if (!summaryData) return;

    const reason = window.prompt('Reason for striking out this procedure:');
    if (!reason) return;

    try {
      await mhrApi.updateProcedure(summaryData.summary.id, procedureId, {
        struck_out: true,
        struck_out_reason: reason,
      } as any);

      setSummaryData(prev => prev ? {
        ...prev,
        procedures: prev.procedures.map(p =>
          p.id === procedureId ? { ...p, struck_out: true, struck_out_reason: reason } : p
        ),
      } : null);
      setSuccess('Procedure struck out');
    } catch (err: any) {
      console.error('[SummaryReview] Error striking out procedure:', err);
      setError(err.response?.data?.error || 'Failed to strike out procedure');
    }
  };

  const handleUndoStrikeOutProcedure = async (procedureId: string) => {
    if (!summaryData) return;

    try {
      await mhrApi.updateProcedure(summaryData.summary.id, procedureId, {
        struck_out: false,
      } as any);

      setSummaryData(prev => prev ? {
        ...prev,
        procedures: prev.procedures.map(p =>
          p.id === procedureId ? { ...p, struck_out: false, struck_out_reason: undefined, struck_out_by: undefined, struck_out_at: undefined } : p
        ),
      } : null);
      setSuccess('Strikeout undone');
    } catch (err: any) {
      console.error('[SummaryReview] Error undoing procedure strikeout:', err);
      setError(err.response?.data?.error || 'Failed to undo strikeout');
    }
  };

  const handleDeleteProcedure = async (procedureId: string) => {
    if (!summaryData) return;

    try {
      await mhrApi.deleteProcedure(summaryData.summary.id, procedureId);

      setSummaryData(prev => prev ? {
        ...prev,
        procedures: prev.procedures.filter(p => p.id !== procedureId),
      } : null);
      setSuccess('Procedure removed');
    } catch (err: any) {
      console.error('[SummaryReview] Error deleting procedure:', err);
      setError(err.response?.data?.error || 'Failed to delete procedure');
    }
  };

  const handleInlineEdit = useCallback(async (
    entryType: 'diagnosis' | 'medication' | 'procedure',
    entryId: string,
    field: string,
    value: string
  ) => {
    if (!summaryData) return;

    // Save previous state for rollback
    const previousData = { ...summaryData };

    // Optimistic update
    setSummaryData(prev => {
      if (!prev) return prev;
      const updated = { ...prev };
      if (entryType === 'diagnosis') {
        updated.diagnoses = prev.diagnoses.map(d =>
          d.id === entryId ? { ...d, [field]: value } : d
        );
      } else if (entryType === 'medication') {
        updated.medications = prev.medications.map(m =>
          m.id === entryId ? { ...m, [field]: value } : m
        );
      } else if (entryType === 'procedure') {
        updated.procedures = prev.procedures.map(p =>
          p.id === entryId ? { ...p, [field]: value } : p
        );
      }
      return updated;
    });

    try {
      const payload = { [field]: value };
      if (entryType === 'diagnosis') {
        await mhrApi.updateDiagnosis(summaryData.summary.id, entryId, payload);
      } else if (entryType === 'medication') {
        await mhrApi.updateMedication(summaryData.summary.id, entryId, payload);
      } else if (entryType === 'procedure') {
        await mhrApi.updateProcedure(summaryData.summary.id, entryId, payload);
      }
    } catch (err: any) {
      // Revert on error
      setSummaryData(previousData);
      setError(err.response?.data?.error || `Failed to update ${entryType}`);
    }
  }, [summaryData]);

  const handleAddEntry = async (data: any) => {
    if (!summaryData) return;

    const { entryType } = addModal;

    if (entryType === 'diagnosis') {
      await mhrApi.addDiagnosis(summaryData.summary.id, data);
    } else if (entryType === 'medication') {
      await mhrApi.addMedication(summaryData.summary.id, data);
    } else if (entryType === 'procedure') {
      await mhrApi.addProcedure(summaryData.summary.id, data);
    }

    await loadSummary();
    setSuccess(`${entryType.charAt(0).toUpperCase() + entryType.slice(1)} added successfully`);
  };

  const handleResolveConflict = async (conflictId: string, resolution: string) => {
    if (!summaryData) return;

    await mhrApi.resolveConflict(summaryData.summary.id, conflictId, resolution);
    await loadSummary();
    setSuccess('Conflict resolved successfully');
  };

  const handleExport = async (format: 'pdf' | 'json') => {
    if (!summaryData) return;

    setExporting(true);
    setError(null);

    try {
      const result = await mhrApi.exportSummary(summaryData.summary.id, format);
      const blob = result.data instanceof Blob ? result.data : new Blob([JSON.stringify(result.data)], { type: result.contentType });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `medical-summary-${documentId}.${format}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      setSuccess(`Summary exported as ${format.toUpperCase()}`);
    } catch (err: any) {
      console.error('[SummaryReview] Error exporting:', err);
      setError(err.response?.data?.error || 'Failed to export summary');
    } finally {
      setExporting(false);
    }
  };

  const handlePageClick = (page: number) => {
    setDocViewerPage(page);
    setDocViewerOpen(true);
  };

  const handleProceedToCriteria = () => {
    const patientId = documentInfo?.patient_id || patientInfo?.id;
    if (currentProtocol && patientId) {
      navigate(
        `/mhr/reviews/new?patient_id=${patientId}&protocol_id=${currentProtocol.id}&document_id=${documentId}`
      );
    } else if (patientId) {
      navigate(`/mhr/reviews/new?patient_id=${patientId}&document_id=${documentId}`);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-primary-600 dark:text-primary-400 animate-spin mx-auto" />
          <p className="text-gray-600 dark:text-gray-400 mt-2">Loading summary...</p>
        </div>
      </div>
    );
  }

  // Error state (when no summary and we had an error)
  if (error && !summaryData) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center gap-4 mb-6">
          <Link
            to="/mhr/upload"
            className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <ArrowLeft size={20} />
          </Link>
          <h1 className="text-lg font-semibold leading-6 text-gray-900 dark:text-gray-100">Medical History Summary</h1>
        </div>

        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6 text-center">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h2 className="text-lg font-medium text-red-800 dark:text-red-300 mb-2">Error Loading Summary</h2>
          <p className="text-red-700 dark:text-red-300 mb-4">{error}</p>
          <button
            onClick={loadSummary}
            className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 min-h-[44px] sm:min-h-0"
          >
            <RefreshCw size={18} />
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // No summary exists - show generate button
  if (!summaryData) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center gap-4 mb-6">
          <Link
            to="/mhr/upload"
            className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <ArrowLeft size={20} />
          </Link>
          <div>
            <h1 className="text-lg font-semibold leading-6 text-gray-900 dark:text-gray-100">Medical History Summary</h1>
            {documentInfo && (
              <p className="text-gray-600 dark:text-gray-400 mt-1">
                {documentInfo.filename} - {documentInfo.page_count} pages
              </p>
            )}
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-8 text-center">
          <Sparkles className="w-16 h-16 text-primary-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">No Summary Generated</h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-md mx-auto">
            This document has not been summarized yet. Generate an AI-powered summary to extract
            diagnoses, medications, and procedures from the medical history.
          </p>
          <button
            onClick={handleGenerateSummary}
            disabled={generating}
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors min-h-[44px] sm:min-h-0"
          >
            {generating ? (
              <>
                <Loader2 size={20} className="animate-spin" />
                Generating Summary...
              </>
            ) : (
              <>
                <Sparkles size={20} />
                Generate Summary
              </>
            )}
          </button>
        </div>
      </div>
    );
  }

  // Main summary view
  const { summary } = summaryData;

  // Build breadcrumb items
  const breadcrumbItems = [
    { label: 'Dashboard', href: '/mhr' },
    { label: 'Upload', href: '/mhr/upload' },
    { label: 'Summary' },
  ];

  return (
    <div className={`${docViewerOpen ? 'flex h-[calc(100vh-6rem)] -m-4' : ''}`}>
      {/* Left panel - summary content */}
      <div className={`${docViewerOpen ? 'flex-1 min-w-0 overflow-y-auto p-4' : ''}`}>
      <div className={`${docViewerOpen ? 'max-w-none' : 'max-w-4xl mx-auto'} space-y-6`}>
      {/* Breadcrumb Navigation */}
      <Breadcrumb items={breadcrumbItems} />

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link
            to="/mhr/upload"
            className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <ArrowLeft size={20} />
          </Link>
          <div>
            <h1 className="text-lg font-semibold leading-6 text-gray-900 dark:text-gray-100">Medical History Summary</h1>
            <div className="flex items-center gap-3 mt-1 text-sm text-gray-600 dark:text-gray-400">
              {patientInfo && (
                <span className="flex items-center gap-1">
                  <User size={14} />
                  {patientInfo.name || patientInfo.internal_id}
                  {patientInfo.mrn && <span className="text-gray-400 dark:text-gray-500 ml-1">(MRN: {patientInfo.mrn})</span>}
                </span>
              )}
              {documentInfo && (
                <span className="flex items-center gap-1">
                  <FileText size={14} />
                  {documentInfo.filename}
                </span>
              )}
              <StatusBadge status={summary.status} />
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={() => setDocViewerOpen(!docViewerOpen)}
            className={`inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg border transition-colors ${
              docViewerOpen
                ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400 border-primary-300 dark:border-primary-700 hover:bg-primary-100 dark:hover:bg-primary-900/30'
                : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
            }`}
            title={docViewerOpen ? 'Close document panel' : 'View source document'}
          >
            {docViewerOpen ? <PanelRightClose size={18} /> : <PanelRightOpen size={18} />}
            <span className="hidden sm:inline">{docViewerOpen ? 'Close Document' : 'View Document'}</span>
          </button>
          <ExportDropdown onExport={handleExport} isExporting={exporting} />
          {canApprove && (
            <button
              onClick={handleOpenApprovalModal}
              disabled={approving}
              className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 min-h-[44px] sm:min-h-0"
            >
              <ShieldCheck size={18} />
              Approve Summary
            </button>
          )}
        </div>
      </div>

      {/* Error alert */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-red-800 dark:text-red-300">{error}</p>
          </div>
          <button onClick={() => setError(null)} className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300">
            <X size={18} />
          </button>
        </div>
      )}

      {/* Approved status banner */}
      {isApproved && summary.approved_at && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <ShieldCheck className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-medium text-green-800 dark:text-green-300">Summary Approved</h3>
              <p className="text-sm text-green-700 dark:text-green-300 mt-1">
                This summary has been reviewed and approved
                {summary.approver_name && ` by ${summary.approver_name}`}
                {summary.approved_at && ` on ${new Date(summary.approved_at).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}`}.
              </p>
              <p className="text-sm text-green-600 dark:text-green-400 mt-2">
                This summary is now locked and cannot be edited.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Summary stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary-100 dark:bg-primary-900/40 rounded-lg">
              <FileText className="w-5 h-5 text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{summary.pages_analyzed}</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">Pages Analyzed</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 dark:bg-green-900/40 rounded-lg">
              <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {Math.round(summary.extraction_confidence * 100)}%
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">Confidence</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 dark:bg-blue-900/40 rounded-lg">
              <CircleDot className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{summary.pages_with_findings}</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">Pages with Findings</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 dark:bg-purple-900/40 rounded-lg">
              <Clock className="w-5 h-5 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {diagnoses.length + medications.length + procedures.length}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">Total Findings</p>
            </div>
          </div>
        </div>
      </div>

      {/* Gap warnings */}
      {summary.potential_gaps && summary.potential_gaps.length > 0 && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-medium text-yellow-800 dark:text-yellow-300">Potential Information Gaps</h3>
              <ul className="mt-2 space-y-1">
                {summary.potential_gaps.map((gap, idx) => (
                  <li key={idx} className="text-sm text-yellow-700 dark:text-yellow-300">
                    {gap}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Conflict alerts */}
      {conflicts.length > 0 && (
        <div className="space-y-3">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
            Conflicts Detected ({conflicts.filter(c => !c.resolved).length} unresolved)
          </h2>
          {conflicts.map(conflict => (
            <ConflictAlert
              key={conflict.id}
              conflict={conflict}
              onResolve={() => setResolveModal({ isOpen: true, conflict })}
            />
          ))}
        </div>
      )}

      {/* Diagnoses section */}
      <CollapsibleSection
        title="Diagnoses"
        icon={Activity}
        count={diagnoses.length}
        iconBgColor="bg-red-100 dark:bg-red-900/40"
        iconColor="text-red-600 dark:text-red-400"
      >
        <div className="space-y-4">
          {activeDiagnoses.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Active / Current</h4>
              <div className="space-y-2">
                {activeDiagnoses.map(diagnosis => (
                  <DiagnosisCard
                    key={diagnosis.id}
                    diagnosis={diagnosis}
                    onFieldSave={(field, value) => handleInlineEdit('diagnosis', diagnosis.id, field, value)}
                    onVerify={() => handleVerifyEntry('diagnosis', diagnosis.id)}
                    onStrikeOut={() => handleStrikeOut(diagnosis.id)}
                    onUndoStrikeOut={() => handleUndoStrikeOut(diagnosis.id)}
                    onDelete={() => handleDeleteDiagnosis(diagnosis.id)}
                    onPageClick={handlePageClick}
                    isVerifying={verifyingId === diagnosis.id}
                    isReadOnly={isApproved}
                  />
                ))}
              </div>
            </div>
          )}

          {historicalDiagnoses.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Historical / Resolved</h4>
              <div className="space-y-2">
                {historicalDiagnoses.map(diagnosis => (
                  <DiagnosisCard
                    key={diagnosis.id}
                    diagnosis={diagnosis}
                    onFieldSave={(field, value) => handleInlineEdit('diagnosis', diagnosis.id, field, value)}
                    onVerify={() => handleVerifyEntry('diagnosis', diagnosis.id)}
                    onStrikeOut={() => handleStrikeOut(diagnosis.id)}
                    onUndoStrikeOut={() => handleUndoStrikeOut(diagnosis.id)}
                    onDelete={() => handleDeleteDiagnosis(diagnosis.id)}
                    onPageClick={handlePageClick}
                    isVerifying={verifyingId === diagnosis.id}
                    isReadOnly={isApproved}
                  />
                ))}
              </div>
            </div>
          )}

          {diagnoses.length === 0 && (
            <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">No diagnoses found</p>
          )}

          {!isApproved && (
            <button
              onClick={() => setAddModal({ isOpen: true, entryType: 'diagnosis' })}
              className="inline-flex items-center gap-2 text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
            >
              <Plus size={16} />
              Add Diagnosis
            </button>
          )}
        </div>
      </CollapsibleSection>

      {/* Medications section */}
      <CollapsibleSection
        title="Medications"
        icon={Pill}
        count={medications.length}
        iconBgColor="bg-blue-100 dark:bg-blue-900/40"
        iconColor="text-blue-600 dark:text-blue-400"
      >
        <div className="space-y-4">
          {currentMedications.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Current / Active</h4>
              <div className="space-y-2">
                {currentMedications.map(medication => (
                  <MedicationCard
                    key={medication.id}
                    medication={medication}
                    onFieldSave={(field, value) => handleInlineEdit('medication', medication.id, field, value)}
                    onVerify={() => handleVerifyEntry('medication', medication.id)}
                    onStrikeOut={() => handleStrikeOutMedication(medication.id)}
                    onUndoStrikeOut={() => handleUndoStrikeOutMedication(medication.id)}
                    onDelete={() => handleDeleteMedication(medication.id)}
                    onPageClick={handlePageClick}
                    isVerifying={verifyingId === medication.id}
                    isReadOnly={isApproved}
                  />
                ))}
              </div>
            </div>
          )}

          {historicalMedications.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Historical / Discontinued</h4>
              <div className="space-y-2">
                {historicalMedications.map(medication => (
                  <MedicationCard
                    key={medication.id}
                    medication={medication}
                    onFieldSave={(field, value) => handleInlineEdit('medication', medication.id, field, value)}
                    onVerify={() => handleVerifyEntry('medication', medication.id)}
                    onStrikeOut={() => handleStrikeOutMedication(medication.id)}
                    onUndoStrikeOut={() => handleUndoStrikeOutMedication(medication.id)}
                    onDelete={() => handleDeleteMedication(medication.id)}
                    onPageClick={handlePageClick}
                    isVerifying={verifyingId === medication.id}
                    isReadOnly={isApproved}
                  />
                ))}
              </div>
            </div>
          )}

          {medications.length === 0 && (
            <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">No medications found</p>
          )}

          {!isApproved && (
            <button
              onClick={() => setAddModal({ isOpen: true, entryType: 'medication' })}
              className="inline-flex items-center gap-2 text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
            >
              <Plus size={16} />
              Add Medication
            </button>
          )}
        </div>
      </CollapsibleSection>

      {/* Procedures section */}
      <CollapsibleSection
        title="Procedures"
        icon={Scissors}
        count={procedures.length}
        iconBgColor="bg-purple-100 dark:bg-purple-900/40"
        iconColor="text-purple-600 dark:text-purple-400"
      >
        <div className="space-y-4">
          {completedProcedures.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Completed</h4>
              <div className="space-y-2">
                {completedProcedures.map(procedure => (
                  <ProcedureCard
                    key={procedure.id}
                    procedure={procedure}
                    onFieldSave={(field, value) => handleInlineEdit('procedure', procedure.id, field, value)}
                    onVerify={() => handleVerifyEntry('procedure', procedure.id)}
                    onStrikeOut={() => handleStrikeOutProcedure(procedure.id)}
                    onUndoStrikeOut={() => handleUndoStrikeOutProcedure(procedure.id)}
                    onDelete={() => handleDeleteProcedure(procedure.id)}
                    onPageClick={handlePageClick}
                    isVerifying={verifyingId === procedure.id}
                    isReadOnly={isApproved}
                  />
                ))}
              </div>
            </div>
          )}

          {plannedProcedures.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Planned / Pending</h4>
              <div className="space-y-2">
                {plannedProcedures.map(procedure => (
                  <ProcedureCard
                    key={procedure.id}
                    procedure={procedure}
                    onFieldSave={(field, value) => handleInlineEdit('procedure', procedure.id, field, value)}
                    onVerify={() => handleVerifyEntry('procedure', procedure.id)}
                    onStrikeOut={() => handleStrikeOutProcedure(procedure.id)}
                    onUndoStrikeOut={() => handleUndoStrikeOutProcedure(procedure.id)}
                    onDelete={() => handleDeleteProcedure(procedure.id)}
                    onPageClick={handlePageClick}
                    isVerifying={verifyingId === procedure.id}
                    isReadOnly={isApproved}
                  />
                ))}
              </div>
            </div>
          )}

          {procedures.length === 0 && (
            <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">No procedures found</p>
          )}

          {!isApproved && (
            <button
              onClick={() => setAddModal({ isOpen: true, entryType: 'procedure' })}
              className="inline-flex items-center gap-2 text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
            >
              <Plus size={16} />
              Add Procedure
            </button>
          )}
        </div>
      </CollapsibleSection>

      {/* Proceed to criteria review */}
      {canProceedToCriteria && (
        <div className="flex justify-end">
          <button
            onClick={handleProceedToCriteria}
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors min-h-[44px] sm:min-h-0"
          >
            Proceed to Criteria Review
            <ChevronRight size={20} />
          </button>
        </div>
      )}

      {/* Approval required message */}
      {!canProceedToCriteria && summary.status !== 'approved' && (
        <div className="bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-primary-600 dark:text-primary-400 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-medium text-primary-800 dark:text-primary-300">Summary Approval Required</h3>
              <p className="text-sm text-primary-700 dark:text-primary-400 mt-1">
                {hasUnresolvedConflicts
                  ? 'Please resolve all conflicts before approving the summary.'
                  : 'Review the extracted information and approve the summary to proceed with criteria evaluation.'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      <AddEntryModal
        isOpen={addModal.isOpen}
        entryType={addModal.entryType}
        summaryId={summary.id}
        onSave={handleAddEntry}
        onClose={() => setAddModal({ isOpen: false, entryType: 'diagnosis' })}
      />

      <ResolveConflictModal
        isOpen={resolveModal.isOpen}
        conflict={resolveModal.conflict}
        onResolve={handleResolveConflict}
        onClose={() => setResolveModal({ isOpen: false, conflict: null })}
      />

      <ApprovalConfirmationModal
        isOpen={approvalModalOpen}
        onConfirm={handleApproveSummary}
        onClose={() => setApprovalModalOpen(false)}
        isApproving={approving}
      />

      {/* Success Toast */}
      <Toast message={success} type="success" onClose={clearSuccess} />
      </div>{/* end max-w wrapper */}
      </div>{/* end left scroll panel */}

      {/* Right panel - Document Viewer */}
      {docViewerOpen && (
        <div className="w-[50%] flex-shrink-0 border-l border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 flex flex-col">
          {/* Panel header */}
          <div className="flex items-center justify-between px-3 py-2 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 flex-shrink-0">
            <div className="flex items-center gap-2 min-w-0">
              <FileText size={16} className="text-gray-500 dark:text-gray-400 flex-shrink-0" />
              <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                {documentInfo?.filename || 'Document'}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <ZoomControls zoom={docViewerZoom} onZoomChange={setDocViewerZoom} />
              <button
                onClick={() => setDocViewerOpen(false)}
                className="p-1.5 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
                aria-label="Close document panel"
              >
                <X size={16} />
              </button>
            </div>
          </div>

          {/* PDF viewer */}
          <div className="flex-1 overflow-hidden relative">
            {pdfLoading ? (
              <div className="flex items-center justify-center h-full bg-gray-50 dark:bg-gray-900/40">
                <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                  <Loader2 className="animate-spin" size={20} />
                  <span className="text-sm">Loading document...</span>
                </div>
              </div>
            ) : pdfUrl ? (
              <PDFViewer
                documentUrl={pdfUrl}
                currentPage={docViewerPage}
                onPageChange={setDocViewerPage}
                onLoadSuccess={setDocViewerTotalPages}
                zoom={docViewerZoom}
              />
            ) : (
              <div className="flex items-center justify-center h-full bg-gray-50 dark:bg-gray-900/40">
                <p className="text-gray-500 dark:text-gray-400 text-sm">No document available</p>
              </div>
            )}
          </div>

          {/* Page navigation footer */}
          <div className="px-3 py-2 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 flex-shrink-0">
            <PageNavigation
              currentPage={docViewerPage}
              totalPages={docViewerTotalPages || documentInfo?.page_count || 0}
              onPageChange={setDocViewerPage}
              pagesWithFindings={pagesWithFindings}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default SummaryReview;
